// The refusals a unit's own key can get from the device auth routes (server/src/platform/identity/routes.ts: claims,
// claim status, device token, and the DPoP check every device call passes), declared once so the server and the
// register agree (security review of platform #1647). The strings are the wire codes exactly as before: this adds
// types, never a new or renamed code.
import { z } from 'zod';

export const DeviceAuthErrorCode = z.enum([
  // The DPoP proof did not verify, or the server wants it signed again with its nonce.
  'invalid_dpop_proof', 'use_dpop_nonce',
  // The server has no record of this unit's key. A node answers it until it has replicated a new activation; the cloud
  // answers it only for a key no account holds.
  'unknown_device',
  // A node that cannot ask the cloud about a unit it does not know (HUB-01/02): 503, try again shortly.
  'hub_unavailable',
  // The unit's key was revoked: a replacement took its record, or someone revoked it in the back office.
  'device_revoked',
  // Its location is held (still on legacy, or gone back after a rollback): the unit waits for the switch-over.
  'device_standby',
  // Activation: no such claim, a key already claimed, or too many tries.
  'claim_not_found', 'key_in_use', 'too_many_attempts',
]);
export type DeviceAuthErrorCodeName = z.infer<typeof DeviceAuthErrorCode>;

/** Each code by name, so a producer or reader never spells one out (`DEVICE_AUTH_ERROR.hub_unavailable`). */
export const DEVICE_AUTH_ERROR = DeviceAuthErrorCode.enum;

/**
 * Why a node refused a unit as `unknown_device`, when it knows (its 401's `error.details.reason`; B1616): `not_here`, the
 * key is not activated at the node's location: its own registry has it at another location, or the cloud, asked, said
 * no (the cloud is authoritative). The unit then
 * stops trying that node and asks the cloud where it syncs. The code stays `unknown_device`, so a register built before
 * the reason reads it as before (it asks the cloud), and a node built before it sends no reason.
 */
export const NODE_REFUSAL_REASON = { not_here: 'not_here' } as const;

/** Which of a unit's servers refused it: its location node, or the cloud itself. */
export type DeviceAuthServer = 'node' | 'cloud';

/**
 * How long apart the cloud's two `unknown_device` answers must be, with no token minted between them, before a unit
 * forgets its identity (`confirm`). Ten minutes (coordinator, 2026-10-23, on the review of platform #2048): a cloud that
 * sits on the wrong database through a few minutes of a deploy must not wipe every unit that asks, and a deleted unit
 * showing Session expired ten minutes late costs nothing.
 */
export const CLOUD_UNKNOWN_CONFIRM_MS = 600_000;

/**
 * What a unit does with a refusal: `retry` later (the server could not tell yet), `reset` (forget its identity and
 * activate again), `standby` (stop selling until the switch-over), `refuse` (this call failed; nothing more), or
 * `confirm` (the cloud found no key: reset only if it says so again).
 * `device_revoked` resets. `unknown_device` from a node is a replication gap, never a loss. From the cloud itself, as a
 * 401, it means no account or location holds the key, which no retry can fix, so the screen shows Session expired
 * (Andrew, A186, 2026-10-23). It is only a lookup that found nothing, though, and a cloud mid-deploy on the wrong
 * database, a broken policy or a stale replica would say the same for every unit at once (security review of platform
 * #2048). So it is `confirm`: the unit forgets its identity only on a second such answer at least
 * CLOUD_UNKNOWN_CONFIRM_MS later, with no token minted between (the register's `activatedClaim`). Any other status
 * carrying the code is a server fault, `retry`. A node's `unknown_device` is taken to the cloud, never acted on alone.
 */
export function deviceAuthAnswer(code: string, server: DeviceAuthServer = 'node', status: number | null = null): 'retry' | 'reset' | 'standby' | 'refuse' | 'confirm' {
  switch (code) {
    case DEVICE_AUTH_ERROR.unknown_device:
      return server === 'cloud' && status === 401 ? 'confirm' : 'retry';
    case DEVICE_AUTH_ERROR.hub_unavailable:
    case DEVICE_AUTH_ERROR.use_dpop_nonce:
    case DEVICE_AUTH_ERROR.too_many_attempts:
      return 'retry';
    case DEVICE_AUTH_ERROR.device_revoked:
      return 'reset';
    case DEVICE_AUTH_ERROR.device_standby:
      return 'standby';
    default:
      return 'refuse';
  }
}
